/**
 * Смена пароля по ссылке из письма.
 *
 * Отдельный экран, а не состояние экрана аккаунта: сюда приходят по ссылке,
 * ещё не войдя, и единственное поле здесь — новый пароль. Сессия после смены
 * не открывается: предъявивший ссылку доказал доступ к почте, а не к аккаунту.
 */

import { useState } from 'react';
import type { Translate } from '@type2x/i18n';
import { Button, Card } from '@type2x/ui';
import { RegistrationError, type RegistrationProblem } from '@type2x/feature-accounts';

export interface PasswordResetScreenProps {
  readonly t: Translate;
  readonly token: string;
  readonly onSubmit: (token: string, password: string) => Promise<void>;
  /** Переход к форме входа: новым паролем нужно войти. */
  readonly onSignIn: () => void;
}

export function PasswordResetScreen({
  t,
  token,
  onSubmit,
  onSignIn,
}: PasswordResetScreenProps): React.JSX.Element {
  const [password, setPassword] = useState('');
  const [problem, setProblem] = useState<RegistrationProblem | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(): Promise<void> {
    setProblem(null);
    setBusy(true);
    try {
      await onSubmit(token, password);
      setDone(true);
    } catch (error) {
      setProblem(error instanceof RegistrationError ? error.problem : 'server-error');
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <Card title={t('reset.title')}>
        <p>{t('reset.done')}</p>
        <Button onClick={onSignIn}>{t('reset.toLogin')}</Button>
      </Card>
    );
  }

  return (
    <Card title={t('reset.title')}>
      <p className="t2x-muted">{t('reset.explain')}</p>

      <label className="t2x-field">
        <span>{t('reset.password')}</span>
        <input
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
      </label>

      {problem !== null && (
        <p role="alert" className="t2x-error">
          {t(`account.error.${problem}`)}
        </p>
      )}

      <Button onClick={() => void submit()} disabled={busy}>
        {t('reset.submit')}
      </Button>
    </Card>
  );
}
